import React, {useCallback, useMemo, useState} from 'react'
import {StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useFocusEffect, useNavigation} from '@react-navigation/native'

import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
  type NavigationProp,
} from '#/lib/routes/types'
import {cleanError} from '#/lib/strings/errors'
import {
  type VideoItem,
  type VideoOrientation,
  type VideoSource,
} from '#/lib/videos'
import {
  flattenVideosPages,
  useVideosQuery,
  type VideosQueryParams,
} from '#/state/queries/videos'
import {useSetMinimalShellMode} from '#/state/shell'
import {List} from '#/view/com/util/List'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon} from '#/components/Button'
import {Check_Stroke2_Corner0_Rounded as Check} from '#/components/icons/Check'
import {Play_Filled_Corner0_Rounded as PlayIcon} from '#/components/icons/Play'
import {SettingsSliderVertical_Stroke2_Corner0_Rounded as SettingsSlider} from '#/components/icons/SettingsSlider'
import * as Layout from '#/components/Layout'
import {ListFooter, ListMaybePlaceholder} from '#/components/Lists'
import * as Menu from '#/components/Menu'
import {Pressable} from '#/components/Pressable'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Videos'>

type ViewMode = 'expanded' | 'compact'

export function VideosScreen({}: Props) {
  const {_} = useLingui()
  const setMinimalShellMode = useSetMinimalShellMode()
  const navigation = useNavigation<NavigationProp>()
  const [viewMode, setViewMode] = useState<ViewMode>('expanded')
  const [isPTRing, setIsPTRing] = useState(false)

  // Filter state
  const [sourceFilter, setSourceFilter] = useState<VideoSource | 'all'>('all')
  const [orientationFilter, setOrientationFilter] = useState<
    VideoOrientation | 'all'
  >('all')

  // Build query params from filter state
  const queryParams = useMemo<VideosQueryParams>(
    () => ({
      source: sourceFilter,
      orientation: orientationFilter,
    }),
    [sourceFilter, orientationFilter],
  )

  // Fetch videos with infinite scroll
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideosQuery(queryParams)

  // Flatten paginated data
  const videos = useMemo(() => flattenVideosPages(data), [data])

  useFocusEffect(
    useCallback(() => {
      setMinimalShellMode(false)
    }, [setMinimalShellMode]),
  )

  const onPressVideo = useCallback(
    (video: VideoItem) => {
      navigation.navigate('VideoWatch', {v: video.id})
    },
    [navigation],
  )

  const onRefresh = useCallback(async () => {
    setIsPTRing(true)
    try {
      await refetch()
    } finally {
      setIsPTRing(false)
    }
  }, [refetch])

  const onEndReached = useCallback(async () => {
    if (isFetchingNextPage || !hasNextPage || isError) return
    try {
      await fetchNextPage()
    } catch {
      // Error handled by query
    }
  }, [isFetchingNextPage, hasNextPage, isError, fetchNextPage])

  const renderItem = useCallback(
    ({item}: {item: VideoItem}) => {
      if (viewMode === 'compact') {
        return (
          <VideoCompactItem video={item} onPress={() => onPressVideo(item)} />
        )
      }
      return (
        <VideoExpandedItem video={item} onPress={() => onPressVideo(item)} />
      )
    },
    [onPressVideo, viewMode],
  )

  const keyExtractor = useCallback((item: VideoItem) => item.id, [])

  // Check if any filters are active
  const hasActiveFilters = sourceFilter !== 'all' || orientationFilter !== 'all'

  return (
    <Layout.Screen testID="videosScreen">
      <Layout.Center>
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Videos</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
          <Layout.Header.Slot>
            <OptionsMenu
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sourceFilter={sourceFilter}
              orientationFilter={orientationFilter}
              onSourceChange={setSourceFilter}
              onOrientationChange={setOrientationFilter}
              hasActiveFilters={hasActiveFilters}
            />
          </Layout.Header.Slot>
        </Layout.Header.Outer>

        {isLoading ? (
          <ListMaybePlaceholder isLoading={true} />
        ) : isError ? (
          <ListMaybePlaceholder
            isLoading={false}
            isError={true}
            errorMessage={cleanError(error)}
            onRetry={refetch}
          />
        ) : videos.length === 0 ? (
          <ListMaybePlaceholder
            isLoading={false}
            emptyType="results"
            emptyTitle={_(msg`No videos found`)}
            emptyMessage={
              hasActiveFilters
                ? _(msg`Try adjusting your filters`)
                : _(msg`No videos available`)
            }
          />
        ) : (
          <List
            data={videos}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            contentContainerStyle={styles.contentContainer}
            initialNumToRender={10}
            desktopFixedHeight
            sideBorders={false}
            refreshing={isPTRing}
            onRefresh={() => void onRefresh()}
            onEndReached={() => void onEndReached()}
            onEndReachedThreshold={2}
            ListFooterComponent={
              <ListFooter
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                error={error ? cleanError(error) : undefined}
                onRetry={fetchNextPage}
                showEndMessage={!hasNextPage && videos.length > 0}
              />
            }
          />
        )}
      </Layout.Center>
    </Layout.Screen>
  )
}

function OptionsMenu({
  viewMode,
  onViewModeChange,
  sourceFilter,
  orientationFilter,
  onSourceChange,
  onOrientationChange,
  hasActiveFilters,
}: {
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  sourceFilter: VideoSource | 'all'
  orientationFilter: VideoOrientation | 'all'
  onSourceChange: (source: VideoSource | 'all') => void
  onOrientationChange: (orientation: VideoOrientation | 'all') => void
  hasActiveFilters: boolean
}) {
  const {_} = useLingui()

  return (
    <Menu.Root>
      <Menu.Trigger label={_(msg`Video options`)}>
        {({props}) => (
          <Button
            {...props}
            label={_(msg`Options`)}
            size="small"
            variant="ghost"
            color={hasActiveFilters ? 'primary' : 'secondary'}
            shape="round"
            style={[a.bg_transparent]}>
            <ButtonIcon icon={SettingsSlider} size="lg" />
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Outer>
        <Menu.Group>
          <Menu.LabelText>
            <Trans>View</Trans>
          </Menu.LabelText>
          <Menu.Item
            label={_(msg`Expanded view`)}
            onPress={() => onViewModeChange('expanded')}>
            {viewMode === 'expanded' && <Menu.ItemIcon icon={Check} />}
            <Menu.ItemText>
              <Trans>Expanded</Trans>
            </Menu.ItemText>
          </Menu.Item>
          <Menu.Item
            label={_(msg`Compact view`)}
            onPress={() => onViewModeChange('compact')}>
            {viewMode === 'compact' && <Menu.ItemIcon icon={Check} />}
            <Menu.ItemText>
              <Trans>Compact</Trans>
            </Menu.ItemText>
          </Menu.Item>
        </Menu.Group>
        <Menu.Divider />
        <Menu.Group>
          <Menu.LabelText>
            <Trans>Source</Trans>
          </Menu.LabelText>
          <Menu.Item
            label={_(msg`All sources`)}
            onPress={() => onSourceChange('all')}>
            {sourceFilter === 'all' && <Menu.ItemIcon icon={Check} />}
            <Menu.ItemText>
              <Trans>All Sources</Trans>
            </Menu.ItemText>
          </Menu.Item>
          <Menu.Item
            label={_(msg`YouTube`)}
            onPress={() => onSourceChange('youtube')}>
            {sourceFilter === 'youtube' && <Menu.ItemIcon icon={Check} />}
            <Menu.ItemText>YouTube</Menu.ItemText>
          </Menu.Item>
          <Menu.Item
            label={_(msg`TikTok`)}
            onPress={() => onSourceChange('tiktok')}>
            {sourceFilter === 'tiktok' && <Menu.ItemIcon icon={Check} />}
            <Menu.ItemText>TikTok</Menu.ItemText>
          </Menu.Item>
          <Menu.Item
            label={_(msg`PeerTube`)}
            onPress={() => onSourceChange('peertube')}>
            {sourceFilter === 'peertube' && <Menu.ItemIcon icon={Check} />}
            <Menu.ItemText>PeerTube</Menu.ItemText>
          </Menu.Item>
        </Menu.Group>
        <Menu.Divider />
        <Menu.Group>
          <Menu.LabelText>
            <Trans>Orientation</Trans>
          </Menu.LabelText>
          <Menu.Item
            label={_(msg`All orientations`)}
            onPress={() => onOrientationChange('all')}>
            {orientationFilter === 'all' && <Menu.ItemIcon icon={Check} />}
            <Menu.ItemText>
              <Trans>All Orientations</Trans>
            </Menu.ItemText>
          </Menu.Item>
          <Menu.Item
            label={_(msg`Horizontal`)}
            onPress={() => onOrientationChange('horizontal')}>
            {orientationFilter === 'horizontal' && (
              <Menu.ItemIcon icon={Check} />
            )}
            <Menu.ItemText>
              <Trans>Horizontal</Trans>
            </Menu.ItemText>
          </Menu.Item>
          <Menu.Item
            label={_(msg`Vertical`)}
            onPress={() => onOrientationChange('vertical')}>
            {orientationFilter === 'vertical' && <Menu.ItemIcon icon={Check} />}
            <Menu.ItemText>
              <Trans>Vertical</Trans>
            </Menu.ItemText>
          </Menu.Item>
        </Menu.Group>
      </Menu.Outer>
    </Menu.Root>
  )
}

const styles = StyleSheet.create({
  contentContainer: {
    paddingBottom: 100,
  },
})

// Single Expanded View (original view)
function VideoExpandedItem({
  video,
  onPress,
}: {
  video: VideoItem
  onPress: () => void
}) {
  const t = useTheme()
  const {_} = useLingui()

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={_(msg`Play ${video.title}`)}
      accessibilityHint={_(msg`Opens video player`)}
      onPress={onPress}
      style={({pressed, hovered}) => [
        a.px_lg,
        a.py_md,
        a.gap_md,
        a.border_b,
        t.atoms.border_contrast_low,
        (pressed || hovered) && t.atoms.bg_contrast_25,
      ]}>
      <View
        style={[
          a.w_full,
          a.rounded_md,
          a.overflow_hidden,
          video.orientation === 'vertical'
            ? {aspectRatio: 9 / 16, maxHeight: 300, alignSelf: 'center'}
            : {aspectRatio: 16 / 9},
          t.atoms.bg_contrast_50,
          a.justify_center,
          a.align_center,
        ]}>
        {video.thumbnail ? (
          <Image
            source={{uri: video.thumbnail}}
            style={[a.absolute, a.inset_0]}
            contentFit="cover"
          />
        ) : null}
        <View
          style={[
            a.rounded_full,
            a.p_md,
            {backgroundColor: 'rgba(0, 0, 0, 0.6)'},
          ]}>
          <PlayIcon size="lg" style={{color: 'white'}} />
        </View>
      </View>
      <View style={[a.gap_xs]}>
        <Text style={[a.text_md, a.font_bold, t.atoms.text]} numberOfLines={2}>
          {video.title}
        </Text>
        <Text
          style={[a.text_sm, t.atoms.text_contrast_medium]}
          numberOfLines={1}>
          {video.source.charAt(0).toUpperCase() + video.source.slice(1)} •{' '}
          {video.orientation === 'vertical' ? (
            <Trans>Vertical</Trans>
          ) : (
            <Trans>Horizontal</Trans>
          )}
        </Text>
      </View>
    </Pressable>
  )
}

// Single Compact View - horizontal layout with smaller thumbnail
function VideoCompactItem({
  video,
  onPress,
}: {
  video: VideoItem
  onPress: () => void
}) {
  const t = useTheme()
  const {_} = useLingui()

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={_(msg`Play ${video.title}`)}
      accessibilityHint={_(msg`Opens video player`)}
      onPress={onPress}
      style={({pressed, hovered}) => [
        a.px_lg,
        a.py_sm,
        a.flex_row,
        a.gap_md,
        a.align_center,
        a.border_b,
        t.atoms.border_contrast_low,
        (pressed || hovered) && t.atoms.bg_contrast_25,
      ]}>
      <View
        style={[
          a.rounded_sm,
          a.overflow_hidden,
          {
            width: 120,
            aspectRatio: 16 / 9,
          },
          t.atoms.bg_contrast_50,
          a.justify_center,
          a.align_center,
        ]}>
        {video.thumbnail ? (
          <Image
            source={{uri: video.thumbnail}}
            style={[a.absolute, a.inset_0]}
            contentFit="cover"
          />
        ) : null}
        <View
          style={[
            a.rounded_full,
            a.p_xs,
            {backgroundColor: 'rgba(0, 0, 0, 0.6)'},
          ]}>
          <PlayIcon size="sm" style={{color: 'white'}} />
        </View>
      </View>
      <View style={[a.flex_1, a.gap_2xs]}>
        <Text style={[a.text_sm, a.font_bold, t.atoms.text]} numberOfLines={2}>
          {video.title}
        </Text>
        <Text
          style={[a.text_xs, t.atoms.text_contrast_medium]}
          numberOfLines={1}>
          {video.source.charAt(0).toUpperCase() + video.source.slice(1)} •{' '}
          {video.orientation === 'vertical' ? (
            <Trans>Vertical</Trans>
          ) : (
            <Trans>Horizontal</Trans>
          )}
        </Text>
      </View>
    </Pressable>
  )
}
